"use client";

import { useState, type FormEvent, type ReactNode } from "react";
import { trackCalculatorUsed } from "@/lib/analytics";
import { fetchDasamsaChart, type DasamsaRequest, type DasamsaResponse } from "./_data";
import { DasamsaResultsSection } from "./_components/DasamsaResultsSection";

export default function DasamsaChartClient() {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DasamsaResponse | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const req: DasamsaRequest = {
        name: name.trim() || "You",
        birth_date: birthDate,
        birth_place: birthPlace.trim(),
      };
      if (birthTime) req.birth_time = birthTime;

      const data = await fetchDasamsaChart(req);
      setResult(data);
      trackCalculatorUsed({ calculator: "dasamsa-chart" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-6 mb-10">
      <DasamsaForm
        name={name}
        birthDate={birthDate}
        birthTime={birthTime}
        birthPlace={birthPlace}
        loading={loading}
        error={error}
        onNameChange={setName}
        onBirthDateChange={setBirthDate}
        onBirthTimeChange={setBirthTime}
        onBirthPlaceChange={setBirthPlace}
        onSubmit={handleSubmit}
      />

      {result && <DasamsaResultsSection result={result} />}
    </div>
  );
}

function DasamsaForm({
  name,
  birthDate,
  birthTime,
  birthPlace,
  loading,
  error,
  onNameChange,
  onBirthDateChange,
  onBirthTimeChange,
  onBirthPlaceChange,
  onSubmit,
}: {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  loading: boolean;
  error: string | null;
  onNameChange: (value: string) => void;
  onBirthDateChange: (value: string) => void;
  onBirthTimeChange: (value: string) => void;
  onBirthPlaceChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <div className="card-sacred p-5 sm:p-6">
      <p
        className="text-[13px] font-semibold uppercase tracking-wide mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        Free Dasamsa Chart Calculator
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <DasamsaFields
          name={name}
          birthDate={birthDate}
          birthTime={birthTime}
          birthPlace={birthPlace}
          onNameChange={onNameChange}
          onBirthDateChange={onBirthDateChange}
          onBirthTimeChange={onBirthTimeChange}
          onBirthPlaceChange={onBirthPlaceChange}
        />

        {error && (
          <p className="text-[13px]" style={{ color: "var(--ish-error, #dc2626)" }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full sm:w-auto px-6 py-2.5 rounded-md text-[14px] font-semibold transition-opacity"
          style={{
            backgroundColor: "var(--ish-accent)",
            color: "#fff",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Computing D10 Chart…" : "Generate Dasamsa Chart"}
        </button>
      </form>
    </div>
  );
}

function DasamsaFields({
  name,
  birthDate,
  birthTime,
  birthPlace,
  onNameChange,
  onBirthDateChange,
  onBirthTimeChange,
  onBirthPlaceChange,
}: {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  onNameChange: (value: string) => void;
  onBirthDateChange: (value: string) => void;
  onBirthTimeChange: (value: string) => void;
  onBirthPlaceChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <DasamsaInput
        id="ds-name"
        label="Name (optional)"
        type="text"
        value={name}
        onChange={onNameChange}
        placeholder="Your name"
        maxLength={100}
      />
      <DasamsaInput
        id="ds-date"
        label="Date of Birth *"
        type="date"
        value={birthDate}
        onChange={onBirthDateChange}
        required
      />
      <DasamsaInput
        id="ds-time"
        label={
          <>
            Time of Birth <span className="font-normal">(needed for accurate D10 lagna)</span>
          </>
        }
        type="time"
        value={birthTime}
        onChange={onBirthTimeChange}
      />
      <DasamsaInput
        id="ds-place"
        label="Place of Birth *"
        type="text"
        value={birthPlace}
        onChange={onBirthPlaceChange}
        placeholder="City, Country"
        required
        maxLength={200}
      />
    </div>
  );
}

function DasamsaInput({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  required,
  maxLength,
}: {
  id: string;
  label: ReactNode;
  type: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[12px] font-medium mb-1"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        className="w-full border rounded-md px-3 py-2 text-[14px] outline-none focus:ring-1"
        style={{
          borderColor: "var(--ish-border)",
          color: "var(--ish-dark)",
          backgroundColor: "var(--ish-surface, #fffdf9)",
        }}
      />
    </div>
  );
}
